import React, { useEffect, useRef, useCallback } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { track } from '../../utils/analytics';
import { AnalyticsEvents } from '../../utils/analyticsEvents';

interface YTPlayer {
  getCurrentTime: () => number;
  getDuration: () => number;
  destroy: () => void;
}

interface YTPlayerEvent {
  data: number;
  target: YTPlayer;
}

declare global {
  interface Window {
    YT?: {
      Player: new (
        elementId: string,
        config: {
          videoId: string;
          playerVars?: Record<string, number | string>;
          events?: {
            onReady?: (e: { target: YTPlayer }) => void;
            onStateChange?: (e: YTPlayerEvent) => void;
          };
        },
      ) => YTPlayer;
      PlayerState: { PLAYING: number; PAUSED: number; ENDED: number };
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}

const VIDEO_ID = 'gKticCyVO54';

interface DemoVideoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DemoVideoModal: React.FC<DemoVideoModalProps> = ({ isOpen, onClose }) => {
  const playerRef = useRef<YTPlayer | null>(null);
  const hasTrackedPlay = useRef(false);
  const hasEnded = useRef(false);
  const prevCallbackRef = useRef<(() => void) | undefined>(undefined);

  const createPlayer = useCallback(() => {
    if (!window.YT?.Player) return;
    if (!document.getElementById('yt-demo-player')) return;
    playerRef.current = new window.YT.Player('yt-demo-player', {
      videoId: VIDEO_ID,
      playerVars: { autoplay: 1, rel: 0, modestbranding: 1 },
      events: {
        onStateChange: (e: YTPlayerEvent) => {
          if (e.data === 1 && !hasTrackedPlay.current) {
            hasTrackedPlay.current = true;
            track(AnalyticsEvents.LANDING_DEMO_PLAY, {
              video_duration: Math.round(e.target.getDuration()),
            });
          }
          if (e.data === 0) {
            hasEnded.current = true;
            track(AnalyticsEvents.LANDING_DEMO_COMPLETE, {
              watch_seconds: Math.round(e.target.getCurrentTime()),
              watch_percent: 100,
              completed: true,
            });
          }
        },
      },
    });
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    hasTrackedPlay.current = false;
    hasEnded.current = false;

    if (window.YT?.Player) {
      createPlayer();
    } else {
      prevCallbackRef.current = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        prevCallbackRef.current?.();
        createPlayer();
      };
      if (!document.getElementById('yt-iframe-api')) {
        const tag = document.createElement('script');
        tag.id = 'yt-iframe-api';
        tag.src = 'https://www.youtube.com/iframe_api';
        document.head.appendChild(tag);
      }
    }

    return () => {
      try { playerRef.current?.destroy(); } catch { /* already destroyed */ }
      playerRef.current = null;
      if (prevCallbackRef.current !== undefined) {
        window.onYouTubeIframeAPIReady = prevCallbackRef.current;
        prevCallbackRef.current = undefined;
      }
    };
  }, [isOpen, createPlayer]);

  const handleClose = useCallback(() => {
    let watchSeconds = 0;
    let watchPercent = 0;
    try {
      const current = playerRef.current?.getCurrentTime() ?? 0;
      const duration = playerRef.current?.getDuration() ?? 0;
      watchSeconds = Math.round(current);
      watchPercent = duration > 0 ? Math.round((current / duration) * 100) : 0;
    } catch { /* player may already be gone */ }

    track(AnalyticsEvents.LANDING_DEMO_CLOSE, {
      watch_seconds: watchSeconds,
      watch_percent: watchPercent,
      completed: hasEnded.current,
    });

    try { playerRef.current?.destroy(); } catch { /* noop */ }
    playerRef.current = null;
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={handleClose}
    >
      <div
        className="relative z-10 w-full max-w-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleClose}
          className="absolute -top-10 right-0 p-1.5 text-white/70 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
          aria-label="Close"
        >
          <XMarkIcon className="w-6 h-6" />
        </button>
        <div
          id="yt-demo-player"
          className="w-full aspect-video rounded-lg overflow-hidden bg-black"
        />
      </div>
    </div>
  );
};

export default DemoVideoModal;
